import { Tooltip, TooltipContent, TooltipTrigger } from "@frontend/components/ui/tooltip"
import {
  describeArcWithCaps,
  INNER_R,
  MID_R,
  OUTER_R,
  polarToCartesian
} from "@frontend/pages/case-workspace/pathology/lib/arc-geometry"
import type { VectorGroup, VectorLabel } from "@frontend/pages/case-workspace/pathology/types"
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"

interface RadialLabelArcProps {
  activeVectorGroups: VectorGroup[]
  activeVectorLabelKey: string | null
  onSelectLabel: (labelKey: string) => void
}

// Fixed arc range (rotated CCW 20° from tool arc)
const ARC_START = 120
const ARC_END = 290

// SVG container: centered on the anchor with generous room
const SVG_SIZE = 250 // CSS pixels
const SVG_HALF = SVG_SIZE / 2
const VB_SIZE = SVG_SIZE * 2 // viewBox units (2× scale to match tool arc)
const VB_HALF = VB_SIZE / 2

// Layout constants (in degrees)
const END_PAD = 10 // dead space at each arc endpoint
const ITEM_SPACING = 26 // degrees between item centers

const SWATCH_SIZE = 38

// Scroll physics
const SCROLL_SENSITIVITY = 0.3 // degrees per pixel of deltaY
const LERP_FACTOR = 0.18

// Separator rendering: radial tick mark
const TICK_HALF_LEN = 12 // half-length of the tick in viewBox units

// Scroll indicator chevrons
const CHEVRON_SIZE = 7 // half-size of the chevron arms

// Pre-computed constant path (all inputs are module-level constants)
const ARC_BAND_PATH = describeArcWithCaps(0, 0, INNER_R, OUTER_R, ARC_START, ARC_END)

const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation()

type FlatItem = { type: "label"; label: VectorLabel; groupName: string } | { type: "separator"; groupKey: string }

export function RadialLabelArc({ activeVectorGroups, activeVectorLabelKey, onSelectLabel }: RadialLabelArcProps) {
  const rawUid = useId()
  const uid = rawUid.replace(/:/g, "")

  // Build flat item list with separators between groups
  const flatItems = useMemo(() => {
    const items: FlatItem[] = []
    activeVectorGroups.forEach((group, gi) => {
      if (gi > 0) {
        items.push({ type: "separator", groupKey: `sep-${group.key}` })
      }
      for (const label of group.labels) {
        items.push({ type: "label", label, groupName: group.name })
      }
    })
    return items
  }, [activeVectorGroups])

  // Scroll state: currentRef for smooth lerp, state for rendering
  const [offset, setOffset] = useState(0)
  const currentRef = useRef(0)
  const targetRef = useRef(0)
  const rafRef = useRef<number>(0)
  const animatingRef = useRef(false)

  // Max scroll offset: ensure last item can reach visible area
  const visibleArcSpan = ARC_END - ARC_START - 2 * END_PAD
  const totalSpan = Math.max(0, (flatItems.length - 1) * ITEM_SPACING)
  const maxOffset = Math.max(0, totalSpan - visibleArcSpan)

  // Reset scroll when groups change
  useEffect(() => {
    targetRef.current = 0
    currentRef.current = 0
    setOffset(0)
    animatingRef.current = false
    cancelAnimationFrame(rafRef.current)
  }, [])

  // Animation loop — only runs when actively animating, uses refs for lerp math
  const startAnimation = useCallback(() => {
    if (animatingRef.current) return
    animatingRef.current = true

    const animate = () => {
      const current = currentRef.current
      const target = targetRef.current
      const diff = target - current
      if (Math.abs(diff) > 0.05) {
        currentRef.current = current + diff * LERP_FACTOR
        setOffset(currentRef.current)
        rafRef.current = requestAnimationFrame(animate)
      } else {
        currentRef.current = target
        setOffset(target)
        animatingRef.current = false
      }
    }
    rafRef.current = requestAnimationFrame(animate)
  }, [])

  // Cleanup rAF on unmount
  useEffect(() => () => cancelAnimationFrame(rafRef.current), [])

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (maxOffset <= 0) return
      e.stopPropagation()
      const newTarget = targetRef.current + e.deltaY * SCROLL_SENSITIVITY
      targetRef.current = Math.max(0, Math.min(maxOffset, newTarget))
      startAnimation()
    },
    [maxOffset, startAnimation]
  )

  // Compute visible items
  const minAngle = ARC_START + END_PAD
  const maxAngle = ARC_END - END_PAD
  const elements: React.ReactNode[] = []

  flatItems.forEach((item, i) => {
    const angle = ARC_END - END_PAD - (i * ITEM_SPACING - offset)
    if (angle < minAngle - ITEM_SPACING / 2 || angle > maxAngle + ITEM_SPACING / 2) return

    if (item.type === "separator") {
      const inner = polarToCartesian(0, 0, MID_R - TICK_HALF_LEN, angle)
      const outer = polarToCartesian(0, 0, MID_R + TICK_HALF_LEN, angle)
      elements.push(
        <line
          key={item.groupKey}
          x1={inner.x}
          y1={inner.y}
          x2={outer.x}
          y2={outer.y}
          stroke="white"
          strokeWidth={3}
          strokeLinecap="round"
          opacity={0.4}
        />
      )
    } else {
      elements.push(
        <LabelSwatch
          key={item.label.key}
          color={item.label.color}
          name={item.label.label}
          groupName={item.groupName}
          isActive={item.label.key === activeVectorLabelKey}
          angle={angle}
          labelKey={item.label.key}
          onClick={onSelectLabel}
          onWheel={handleWheel}
        />
      )
    }
  })

  // Scroll indicator chevrons at endcaps
  if (maxOffset > 0) {
    if (offset > 0.5) {
      elements.push(<ScrollChevron key="chev-end" angle={ARC_END + END_PAD / 2} direction={1} />)
    }
    if (offset < maxOffset - 0.5) {
      elements.push(<ScrollChevron key="chev-start" angle={ARC_START - END_PAD / 2} direction={-1} />)
    }
  }

  return (
    <div
      className="absolute"
      style={{
        left: -SVG_HALF,
        top: -SVG_HALF,
        width: SVG_SIZE,
        height: SVG_SIZE,
        pointerEvents: "none"
      }}
    >
      <svg
        width={SVG_SIZE}
        height={SVG_SIZE}
        viewBox={`-${VB_HALF} -${VB_HALF} ${VB_SIZE} ${VB_SIZE}`}
        className="animate-in fade-in-0 zoom-in-95 duration-150"
        style={{ pointerEvents: "none" }}
      >
        <defs>
          <filter id={`la-shadow-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="black" floodOpacity="0.35" />
          </filter>
        </defs>

        {/* Band background — also captures wheel events */}
        <path
          d={ARC_BAND_PATH}
          fill="hsl(var(--card) / 0.92)"
          stroke="hsl(var(--border) / 0.4)"
          strokeWidth={1}
          filter={`url(#la-shadow-${uid})`}
          style={{ pointerEvents: "auto" }}
          onWheel={handleWheel}
        />

        {elements}
      </svg>
    </div>
  )
}

// ── Scroll indicator chevron with subtle bob ─────────────────────

function ScrollChevron({
  angle,
  direction
}: {
  angle: number
  direction: 1 | -1 // 1 = toward ARC_END, -1 = toward ARC_START
}) {
  const pos = polarToCartesian(0, 0, MID_R, angle)
  // Tangent direction along the arc (perpendicular to radius, clockwise)
  const radAngle = ((angle - 90) * Math.PI) / 180
  // Tangent points along increasing angle
  const tx = -Math.sin(radAngle) * direction
  const ty = Math.cos(radAngle) * direction
  // Perpendicular to tangent (radial outward)
  const px = Math.cos(radAngle)
  const py = Math.sin(radAngle)

  // Chevron: two arms forming a V pointing along tangent
  const tip = { x: pos.x + tx * CHEVRON_SIZE, y: pos.y + ty * CHEVRON_SIZE }
  const arm1 = {
    x: pos.x - tx * CHEVRON_SIZE * 0.3 + px * CHEVRON_SIZE,
    y: pos.y - ty * CHEVRON_SIZE * 0.3 + py * CHEVRON_SIZE
  }
  const arm2 = {
    x: pos.x - tx * CHEVRON_SIZE * 0.3 - px * CHEVRON_SIZE,
    y: pos.y - ty * CHEVRON_SIZE * 0.3 - py * CHEVRON_SIZE
  }

  return (
    <g opacity={0.4}>
      <polyline
        points={`${arm1.x},${arm1.y} ${tip.x},${tip.y} ${arm2.x},${arm2.y}`}
        fill="none"
        stroke="white"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <animateTransform
        attributeName="transform"
        type="translate"
        values={`0,0; ${tx * 3},${ty * 3}; 0,0`}
        dur="1.8s"
        repeatCount="indefinite"
      />
    </g>
  )
}

// ── Label color swatch with group tooltip ────────────────────────

function LabelSwatch({
  color,
  name,
  groupName,
  isActive,
  angle,
  labelKey,
  onClick,
  onWheel
}: {
  color: string
  name: string
  groupName: string
  isActive: boolean
  angle: number
  labelKey: string
  onClick: (key: string) => void
  onWheel: (e: React.WheelEvent) => void
}) {
  const pos = polarToCartesian(0, 0, MID_R, angle)
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onClick(labelKey)
    },
    [onClick, labelKey]
  )

  return (
    <foreignObject
      x={pos.x - SWATCH_SIZE / 2}
      y={pos.y - SWATCH_SIZE / 2}
      width={SWATCH_SIZE}
      height={SWATCH_SIZE}
      style={{ pointerEvents: "auto", overflow: "visible" }}
      onWheel={onWheel}
    >
      <Tooltip>
        <TooltipTrigger
          onClick={handleClick}
          onPointerDown={stopPropagation}
          className={`w-full h-full rounded-full flex items-center justify-center transition-all ${
            isActive ? "ring-[3px] ring-primary ring-offset-1 ring-offset-background scale-110" : "hover:scale-105"
          }`}
        >
          <div
            className="rounded-full"
            style={{
              backgroundColor: color,
              width: isActive ? 28 : 24,
              height: isActive ? 28 : 24,
              boxShadow: "0 0 0 2px hsl(var(--card)), 0 1px 3px rgba(0,0,0,0.3)"
            }}
          />
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <div className="flex flex-col">
            <span className="text-muted-foreground text-[10px] leading-tight">{groupName}</span>
            <span className="leading-tight">{name}</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </foreignObject>
  )
}
